import React from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet'

import Layout from '../components/layout'
import Icon from '../components/icon'
import ChatMockup from '../components/chat-mockup'
import { SITE, WA_PROYECTO, WA_SECRETARIO } from '../site'
import { CATEGORIES } from '../secretario/catalog'
import './home.css'

const HERO_CHAT = [
  { from: 'user', text: 'Agéndame una reunión con Ana mañana a las 3 p. m. con Meet', time: '9:41' },
  {
    from: 'bot',
    text: 'Voy a crear «Reunión con Ana» mañana de 3:00 a 3:30 p. m., con enlace de Meet e invitación para ana@empresa.com. Para hacerlo, respóndeme «sí».',
    time: '9:41',
  },
  { from: 'user', text: 'sí', time: '9:42' },
  { from: 'bot', text: 'Listo. La reunión quedó en tu calendario y Ana recibió la invitación.', time: '9:42' },
]

const SECRETARIO_FEATURES = [
  'Agenda, mueve y cancela reuniones con videollamada',
  'Envía correos en tu nombre',
  'Crea tareas y te recuerda lo importante',
  'Programa encargos que se repiten, como tu agenda de cada lunes',
  'Crea documentos, hojas de cálculo y formularios, y los comparte',
]

// Las categorías del catálogo de Secretario; los conectores de cada una están en sus Funcionalidades.
const WORKS_WITH = CATEGORIES.map((c) => c.name)

const SERVICES = [
  {
    icon: 'bot',
    title: 'Agentes a la medida',
    text: 'Agentes de ventas y soporte que atienden a tus clientes por WhatsApp las 24 horas, entrenados con la información de tu negocio y conectados a tus sistemas.',
  },
  {
    icon: 'workflow',
    title: 'Automatización de procesos',
    text: 'Conectamos tus herramientas —correo, CRM, hojas de cálculo y bases de datos— para eliminar tareas manuales y errores repetitivos.',
  },
  {
    icon: 'cloud',
    title: 'DevOps y Cloud',
    text: 'Infraestructura en la nube, Kubernetes, integración y despliegue continuos y optimización de costos, para que tus sistemas escalen sin sorpresas.',
  },
]

const FOUNDERS = [
  {
    initials: 'EG',
    name: 'Efrain Galvis',
    role: 'Cofundador',
    bio: 'Ingeniero DevOps, Cloud y de automatización con 9 años de experiencia en infraestructura en la nube, optimización de costos y automatización de flujos.',
    quote: 'Siempre que puedas imaginarlo, puedes automatizarlo. Qué tan difícil sea es otro asunto: atrévete a hacer realidad tus ideas.',
  },
  {
    initials: 'HG',
    name: 'Hermann Galvis',
    role: 'Cofundador',
    bio: 'Ingeniero DevOps, Cloud y de automatización con 6 años de experiencia en infraestructura en la nube y automatización de flujos.',
    quote: 'Creamos agentes que no solo entienden tu negocio, sino que lo representan, con el tono y el conocimiento de tu marca.',
  },
]

const Home = () => (
  <Layout>
    <Helmet>
      <title>Variant · Agentes de inteligencia artificial</title>
      <meta
        name="description"
        content="Variant crea agentes de inteligencia artificial que automatizan el trabajo repetitivo de personas y empresas. Conoce Secretario, tu asistente personal en WhatsApp."
      />
      <link rel="canonical" href={`${SITE.url}/`} />
      <meta property="og:title" content="Variant · Agentes de inteligencia artificial" />
      <meta property="og:url" content={`${SITE.url}/`} />
    </Helmet>

    <section className="hero">
      <div className="container hero__grid">
        <div>
          <span className="eyebrow">Agentes de inteligencia artificial</span>
          <h1 className="hero__title">Automatizamos el trabajo repetitivo para que te enfoques en lo importante</h1>
          <p className="hero__lead">
            En Variant creamos agentes de IA que se conectan con las herramientas que ya usas y hacen el trabajo por ti,
            para personas y para empresas.
          </p>
          <div className="btn-row">
            <Link to="/secretario" className="btn btn--primary">
              Conoce Secretario <Icon name="arrow" size={18} />
            </Link>
            <a href={WA_PROYECTO} className="btn btn--ghost" target="_blank" rel="noopener noreferrer">
              Hablemos de tu proyecto
            </a>
          </div>
        </div>
        <ChatMockup messages={HERO_CHAT} />
      </div>
    </section>

    <section className="section section--soft" id="productos">
      <div className="container">
        <div className="section-head">
          <span className="eyebrow">Productos</span>
          <h2 className="section-title">Listos para usar desde hoy</h2>
          <p className="section-lead">Herramientas construidas por Variant que resuelven un problema concreto, sin implementaciones largas.</p>
        </div>

        <article className="product">
          <div className="product__main">
            <span className="badge">Acceso anticipado</span>
            <h3 className="product__name">Secretario</h3>
            <p className="product__tagline">Tu asistente personal en WhatsApp</p>
            <p className="product__text">
              Pídele por mensaje o nota de voz lo que necesitas y Secretario lo resuelve con las herramientas que ya usas:
              tu correo, tu calendario, tus tareas y tus documentos. Antes de hacer algo en tu nombre, te pide
              confirmación.
            </p>
            <ul className="check-list">
              {SECRETARIO_FEATURES.map((f) => (
                <li key={f}>
                  <Icon name="check" size={18} />
                  {f}
                </li>
              ))}
            </ul>
            <div className="btn-row product__actions">
              <Link to="/secretario" className="btn btn--primary">
                Ver Secretario <Icon name="arrow" size={18} />
              </Link>
              <a href={WA_SECRETARIO} className="btn btn--ghost" target="_blank" rel="noopener noreferrer">
                Solicitar acceso
              </a>
            </div>
          </div>
          <div className="product__side">
            <span className="product__side-title">Te ayuda con</span>
            <ul className="pill-list">
              {WORKS_WITH.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
            <div className="product__note">
              <Icon name="shield" size={20} />
              <p>Usa solo los permisos que necesita y no puede leer tu bandeja de entrada.</p>
            </div>
          </div>
        </article>
      </div>
    </section>

    <section className="section" id="servicios">
      <div className="container">
        <div className="section-head">
          <span className="eyebrow">Servicios para empresas</span>
          <h2 className="section-title">Soluciones a la medida de tu operación</h2>
          <p className="section-lead">Diseñamos, construimos y operamos la tecnología que tu equipo necesita para crecer sin sumar trabajo manual.</p>
        </div>
        <div className="grid-3">
          {SERVICES.map((s) => (
            <div key={s.title} className="card">
              <span className="icon-chip">
                <Icon name={s.icon} size={22} />
              </span>
              <h3>{s.title}</h3>
              <p>{s.text}</p>
            </div>
          ))}
        </div>
        <div className="services__cta">
          <a href={WA_PROYECTO} className="btn btn--ghost" target="_blank" rel="noopener noreferrer">
            Cotizar un proyecto <Icon name="arrow" size={18} />
          </a>
        </div>
      </div>
    </section>

    <section className="section section--soft" id="nosotros">
      <div className="container">
        <div className="section-head">
          <span className="eyebrow">Nosotros</span>
          <h2 className="section-title">Ingenieros que automatizan desde hace años</h2>
          <p className="section-lead">
            Variant es una empresa de ingeniería de Bogotá, Colombia. Unimos experiencia en infraestructura en la nube y
            automatización con inteligencia artificial para construir soluciones confiables.
          </p>
        </div>
        <div className="founders">
          {FOUNDERS.map((f) => (
            <figure key={f.name} className="founder card">
              <blockquote>“{f.quote}”</blockquote>
              <figcaption>
                <span className="founder__avatar">{f.initials}</span>
                <span>
                  <strong>{f.name}</strong>
                  <span className="founder__role">{f.role}, Variant</span>
                </span>
              </figcaption>
              <p className="founder__bio">{f.bio}</p>
            </figure>
          ))}
        </div>
      </div>
    </section>

    <section className="section" id="contacto">
      <div className="container">
        <div className="cta-band">
          <h2>¿Tienes un proceso que quieras automatizar?</h2>
          <p>Cuéntanos qué te quita tiempo y te proponemos cómo resolverlo. Te respondemos por WhatsApp o por correo.</p>
          <div className="btn-row">
            <a href={WA_PROYECTO} className="btn btn--primary" target="_blank" rel="noopener noreferrer">
              <Icon name="chat" size={18} /> Escríbenos por WhatsApp
            </a>
            <a href={`mailto:${SITE.email}`} className="btn btn--ghost">
              <Icon name="mail" size={18} /> {SITE.email}
            </a>
          </div>
        </div>
      </div>
    </section>
  </Layout>
)

export default Home
